import * as Sentry from "@sentry/react";

export type ErrorSeverity = "info" | "warning" | "error" | "fatal";

// ─── Sec4: PII / secret scrubber for Sentry payloads ──────────────────────
// LGPD compliance: prevents transcripts, audio, BYOK keys, and auth tokens
// from ever reaching Sentry, even if upstream code accidentally captures them.

const REDACTED = "[redacted]";
const REDACTED_KEY = "[redacted-key]";

/** Property names whose values are always replaced. */
const SENSITIVE_KEYS = new Set([
  "transcript",
  "transcription",
  "audio_url",
  "apikey",
  "apiKey",
  "api_key",
  "byok_key",
  "byokKey",
  "authorization",
  "Authorization",
  "password",
  "email",
]);

/** URL substrings that indicate a request body that must be wholesale scrubbed. */
const SENSITIVE_URL_HINTS = ["/transcribe", "/analyze-creative", "/byok-test"];

/** Inline secret patterns — replaced inside any string we scrub. */
const SECRET_PATTERNS: Array<RegExp> = [
  /sk-[a-zA-Z0-9_-]{20,}/g, // OpenAI keys
  /sk_(live|test)_[a-zA-Z0-9]+/g, // Stripe
  /eyJ[a-zA-Z0-9._-]+/g, // JWTs
];

function scrubString(input: string): string {
  let out = input;
  for (const re of SECRET_PATTERNS) {
    out = out.replace(re, REDACTED_KEY);
  }
  return out;
}

function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 6) return REDACTED;
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return scrubString(value);
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.has(k)) {
      out[k] = REDACTED;
    } else {
      out[k] = scrubValue(v, depth + 1);
    }
  }
  return out;
}

function isSensitiveUrl(url: string | undefined): boolean {
  if (!url) return false;
  return SENSITIVE_URL_HINTS.some((hint) => url.includes(hint));
}

/**
 * Sentry `beforeSend` hook — runs on every event/exception before send.
 * Mutates a deep clone (Sentry expects the same shape returned).
 */
function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  try {
    // Scrub request body / URL data.
    if (event.request) {
      if (isSensitiveUrl(event.request.url)) {
        event.request.data = REDACTED;
      } else if (event.request.data !== undefined) {
        event.request.data = scrubValue(event.request.data) as typeof event.request.data;
      }
      if (event.request.headers) {
        event.request.headers = scrubValue(event.request.headers) as typeof event.request.headers;
      }
    }

    // Scrub `extra` and `contexts` recursively.
    if (event.extra) {
      event.extra = scrubValue(event.extra) as typeof event.extra;
    }
    if (event.contexts) {
      event.contexts = scrubValue(event.contexts) as typeof event.contexts;
    }
    if (event.tags) {
      event.tags = scrubValue(event.tags) as typeof event.tags;
    }

    // Scrub breadcrumbs (data + message).
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((b) => {
        const next = { ...b };
        const messageHasSensitiveUrl =
          typeof next.message === "string" && isSensitiveUrl(next.message);
        if (typeof next.message === "string") {
          next.message = scrubString(next.message);
        }
        if (next.data) {
          const isObj = typeof next.data === "object";
          const dataUrl = isObj && "url" in next.data ? String(next.data.url) : "";
          const sensitive = messageHasSensitiveUrl || isSensitiveUrl(dataUrl);
          if (sensitive) {
            next.data = { url: dataUrl || undefined, body: REDACTED } as typeof next.data;
          } else {
            next.data = scrubValue(next.data) as typeof next.data;
          }
        }
        return next;
      });
    }

    // Scrub message body for non-Error events.
    if (event.message) {
      event.message = scrubString(event.message);
    }

    // Scrub exception values (the message + stack-stored args sometimes leak keys).
    if (event.exception?.values) {
      for (const ex of event.exception.values) {
        if (ex.value) ex.value = scrubString(ex.value);
      }
    }
  } catch (err) {
    // Never let the scrubber throw — at worst we send the un-scrubbed event,
    // but more importantly we never want to drop real error reports.
    console.warn("[error-report] beforeSend scrubber failed:", err);
  }
  return event;
}

// Exported for unit tests.
export const __testing = { beforeSend, scrubValue, scrubString };

export type ErrorReportContext = {
  boundary?: string;
  componentStack?: string;
  route?: string;
  severity?: ErrorSeverity;
  source?: string;
  tags?: Record<string, string>;
};

export type ErrorReportPayload = {
  message: string;
  name: string;
  stack?: string;
  timestamp: string;
  context: ErrorReportContext;
};

type GlobalHandlerCleanup = () => void;

let initialized = false;
let sentryInitialized = false;
let cleanupGlobalHandlers: GlobalHandlerCleanup | null = null;

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Unknown non-Error rejection");
};

const getSentryDsn = () => {
  try {
    return import.meta.env.VITE_SENTRY_DSN;
  } catch {
    return undefined;
  }
};

declare const __APP_VERSION__: string;

const initSentry = () => {
  const dsn = getSentryDsn();

  if (!dsn || sentryInitialized) {
    return Boolean(dsn);
  }

  const isProd = import.meta.env.MODE === "production";

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: typeof __APP_VERSION__ === "string" ? `cdp-frontend@${__APP_VERSION__}` : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: isProd ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend,
  });

  sentryInitialized = true;
  return true;
};

export const setSentryUser = (user: { id: string } | null) => {
  if (!initSentry()) return;
  Sentry.setUser(user);
};

export const setSentryTag = (key: string, value: string) => {
  if (!initSentry()) return;
  Sentry.setTag(key, value);
};

export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
) => {
  if (!initSentry()) return;
  Sentry.addBreadcrumb({ category, message, level: "info", data });
};

export const reportError = (error: unknown, context: ErrorReportContext = {}) => {
  const normalizedError = normalizeError(error);
  const payload: ErrorReportPayload = {
    message: normalizedError.message,
    name: normalizedError.name,
    stack: normalizedError.stack,
    timestamp: new Date().toISOString(),
    context: {
      severity: "error",
      ...context,
    },
  };

  // Always emit structured console log (boundaries/tests/local debugging
  // rely on this contract). Sentry capture runs on top when DSN configured.
  console.error("[error-report]", payload);

  if (initSentry()) {
    Sentry.captureException(normalizedError, {
      level: payload.context.severity,
      tags: payload.context.tags,
      contexts: {
        errorReport: payload.context,
      },
      extra: {
        boundary: payload.context.boundary,
        componentStack: payload.context.componentStack,
        route: payload.context.route,
        source: payload.context.source,
        timestamp: payload.timestamp,
      },
    });
  }

  return payload;
};

export const initErrorReporting = () => {
  if (initialized || typeof window === "undefined") {
    return cleanupGlobalHandlers ?? (() => undefined);
  }

  initSentry();

  const onError = (event: ErrorEvent) => {
    reportError(event.error ?? event.message, {
      source: "window.onerror",
      severity: "fatal",
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportError(event.reason, {
      source: "window.unhandledrejection",
      severity: "fatal",
    });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  initialized = true;
  cleanupGlobalHandlers = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    initialized = false;
    cleanupGlobalHandlers = null;
  };

  return cleanupGlobalHandlers;
};
